import { useEffect, useState } from "react";
import { Box, Button, Drawer, IconButton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import dayjs, { Dayjs } from "dayjs";
import { RangeCalendarHeader } from "../../Dashboard/helpers/RangeCalendarHeader";
import { isDateRangeInvalid } from "../constants";
import { colors, spacing, typography } from "../../../../assets/styles/tokens";

interface LeadsMobileDateRangeDrawerProps {
  open: boolean;
  onClose: () => void;
  start: string;
  end: string;
  onApply: (start: string, end: string) => void;
}

function toDayjs(value: string): Dayjs | null {
  return value ? dayjs(value) : null;
}

function toIsoDate(value: Dayjs | null): string {
  return value && value.isValid() ? value.format("YYYY-MM-DD") : "";
}

// Same bottom-sheet calendar pattern as the Performance dashboard's
// PerformanceDateTimeDrawer.tsx (Puller + X close + title + calendar +
// Cancel/Apply), stripped down to just the calendar view — this module has
// no primary-timeframe list or "Compare to" feature, only a start/end range.
// Reuses that module's RangeCalendarHeader (month/year quick-jump) as-is.
export default function LeadsMobileDateRangeDrawer({
  open,
  onClose,
  start,
  end,
  onApply,
}: LeadsMobileDateRangeDrawerProps) {
  const [stagedStart, setStagedStart] = useState<Dayjs | null>(toDayjs(start));
  const [stagedEnd, setStagedEnd] = useState<Dayjs | null>(toDayjs(end));

  useEffect(() => {
    if (open) {
      setStagedStart(toDayjs(start));
      setStagedEnd(toDayjs(end));
    }
  }, [open, start, end]);

  const isRangeValid =
    !!stagedStart &&
    !!stagedEnd &&
    !isDateRangeInvalid(toIsoDate(stagedStart), toIsoDate(stagedEnd));

  function handleChange(newValue: [Dayjs | null, Dayjs | null]) {
    const [nextStart, nextEnd] = newValue;
    if (nextStart && nextEnd && isDateRangeInvalid(toIsoDate(nextStart), toIsoDate(nextEnd))) return;
    setStagedStart(nextStart);
    setStagedEnd(nextEnd);
  }

  function handleApply() {
    if (!isRangeValid) return;
    onApply(toIsoDate(stagedStart), toIsoDate(stagedEnd));
    onClose();
  }

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderTopLeftRadius: spacing.Radius.xl.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            p: `${spacing.Spacing.xl.$value} !important`,
          },
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 4,
          borderRadius: spacing.Radius.md.$value,
          bgcolor: colors.GrayLightMode[300].$value,
          mx: "auto",
        }}
      />
      <Stack justifyContent="flex-end" direction="row" sx={{ mt: spacing.Spacing.md.$value }}>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ color: colors.GrayLightMode[700].$value }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Typography
        sx={{
          fontSize: typography.Size.Text.sm.$value,
          color: colors.GrayLightMode[700].$value,
          mb: spacing.Spacing.md.$value,
        }}
      >
        Select Date Range
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", mb: spacing.Spacing.md.$value }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <StaticDateRangePicker
            calendars={1}
            disableFuture
            value={[stagedStart, stagedEnd]}
            onChange={handleChange}
            slots={{ calendarHeader: RangeCalendarHeader }}
            slotProps={{ actionBar: { actions: [] }, toolbar: { hidden: true } }}
            sx={{
              "& .MuiPickersDay-root.Mui-selected": {
                backgroundColor: `${colors.Brand[600].$value} !important`,
              },
              "& .MuiDateRangePickersDay-rangeIntervalDayHighlight": {
                backgroundColor: colors.Brand[100].$value,
              },
              "& .MuiPickersDay-root:hover, & .MuiPickersDay-root:focus": {
                backgroundColor: colors.Brand[100].$value,
              },
              "& .MuiPickersDay-today": {
                borderColor: colors.Brand[600].$value,
              },
            }}
          />
        </LocalizationProvider>
      </Box>

      <Stack direction="row" spacing={spacing.Spacing.md.$value}>
        <Button
          fullWidth
          variant="outlined"
          sx={{
            textTransform: "capitalize",
            borderRadius: spacing.Radius.md.$value,
            color: colors.Brand[600].$value,
            borderColor: colors.Brand[600].$value,
            "&:hover": { borderColor: colors.Brand[700].$value, backgroundColor: colors.Brand[25].$value },
          }}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          variant="contained"
          disabled={!isRangeValid}
          sx={{
            textTransform: "capitalize",
            boxShadow: "none !important",
            background: colors.Brand[600].$value,
            borderRadius: spacing.Radius.md.$value,
            "&:hover": { background: colors.Brand[700].$value },
          }}
          onClick={handleApply}
        >
          Apply
        </Button>
      </Stack>
    </Drawer>
  );
}
